import { useEffect, useId, useState, type JSX } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/base/buttons/button";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Modal } from "@/components/application/modals/modal-controlled";
import {
  type AvailableBudgetTarget,
  type CreateBudgetRequest,
  decodeBudgetTargetKey,
  encodeBudgetTargetKey,
  formatBudgetMonth,
} from "../../schemas/budget";

/**
 * UC-002 Krok 1 — "Nový rozpočet" dialog: vyber (měsíc, Středisko) a vytvoř draft.
 *
 * Loading → empty state (EC5) | populated dropdown.
 * Princip #4 (sync transparency): empty state odkazuje na /admin/sync
 * a vysvětluje, proč není co nabídnout.
 * Princip #6 — endpoint je globalAdmin-only (gate na BE).
 */
export interface CreateBudgetDialogProps {
  open: boolean;
  targets: AvailableBudgetTarget[];
  loading?: boolean;
  submitting?: boolean;
  /** Error code (BUDGET_ALREADY_EXISTS / BUDGET_MONTH_NOT_CLOSED / …) → mapping v dialogu. */
  submitErrorCode?: string | null;
  submitErrorMessage?: string | null;
  onCancel: () => void;
  onConfirm: (payload: CreateBudgetRequest) => void;
}

function mapErrorCode(code: string | null | undefined, fallback: string | null | undefined): string {
  if (!code) return fallback ?? "Chyba při vytváření rozpočtu.";
  switch (code) {
    case "BUDGET_MONTH_NOT_CLOSED":
      return "Měsíc ještě není uzavřený. Vyúčtování bude možné od 1. dne následujícího měsíce.";
    case "BUDGET_PREREQUISITES_FAILED":
      return "Sync pro toto Středisko není kompletní za daný měsíc. Zkontrolujte stav syncu.";
    case "BUDGET_BILLING_GROUP_NOT_FOUND":
      return "Vybrané Středisko nebylo nalezeno.";
    case "BUDGET_ALREADY_EXISTS":
      return "Rozpočet pro tuto kombinaci už existuje. Otevřete jej v seznamu nebo aktualizujte dialog.";
    case "BUDGET_SERIALIZATION_RETRY_EXHAUSTED":
      return "Vytvoření se nepodařilo kvůli souběhu — zkuste znovu za chvíli.";
    default:
      return fallback ?? `Chyba: ${code}`;
  }
}

export function CreateBudgetDialog(props: CreateBudgetDialogProps) {
  const {
    open,
    targets,
    loading,
    submitting,
    submitErrorCode,
    submitErrorMessage,
    onCancel,
    onConfirm,
  } = props;

  const [selectedKey, setSelectedKey] = useState<string>("");
  const selectId = useId();
  const hintId = useId();

  useEffect(() => {
    if (open) setSelectedKey("");
  }, [open]);

  const hasTargets = targets.length > 0;
  const isEmpty = !loading && !hasTargets;
  const selectedTarget = targets.find((t) => encodeBudgetTargetKey(t) === selectedKey) ?? null;

  function handleConfirm(): void {
    const decoded = decodeBudgetTargetKey(selectedKey);
    if (!decoded) return;
    onConfirm({ billing_group_id: decoded.billing_group_id, month: decoded.month });
  }

  function renderBody(): JSX.Element {
    if (loading) {
      return (
        <div data-testid="create-budget-loading" className="flex items-center gap-3 py-4">
          <LoadingIndicator size="md" label="Načítám dostupné kombinace…" />
          <span className="text-tertiary">Načítám dostupné kombinace…</span>
        </div>
      );
    }
    if (isEmpty) {
      return (
        <div data-testid="create-budget-empty">
          <InlineAlert variant="info" title="Žádná dvojice k vyúčtování">
            Buď nejsou uzavřené měsíce s kompletním stavem syncu, nebo už jsou
            všechny rozpočty vytvořené. Zkontrolujte stav syncu, případně
            počkejte na uzavření aktuálního měsíce.
          </InlineAlert>
          <p className="mt-3">
            <Link
              to="/admin/sync"
              className="text-brand-secondary font-medium hover:underline"
              data-testid="create-budget-sync-link"
            >
              Otevřít stav syncu
            </Link>
          </p>
        </div>
      );
    }
    return (
      <div data-testid="create-budget-form" className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor={selectId} className="text-sm font-medium text-secondary">
            Měsíc a Středisko{" "}
            <span aria-hidden="true" className="text-error-primary">
              *
            </span>
          </label>
          <select
            id={selectId}
            className="block w-full min-h-touch rounded-lg bg-primary px-3 py-2 text-md text-primary ring-1 ring-inset ring-primary outline-focus-ring focus:ring-2 focus:ring-brand disabled:opacity-50"
            value={selectedKey}
            onChange={(e) => setSelectedKey(e.target.value)}
            aria-describedby={hintId}
            aria-required="true"
            data-testid="create-budget-select"
            disabled={submitting}
          >
            <option value="">— vyberte —</option>
            {targets.map((t) => {
              const key = encodeBudgetTargetKey(t);
              return (
                <option key={key} value={key}>
                  {formatBudgetMonth(t.month)} — {t.billing_group_code} ({t.zone_code})
                </option>
              );
            })}
          </select>
          <p id={hintId} className="text-sm text-tertiary">
            Rozpočet je per-Středisko. Středisko 1 a 2 lze schvalovat nezávisle.
          </p>
        </div>
        {submitErrorCode && (
          <InlineAlert variant="error" title="Nelze vytvořit rozpočet">
            {mapErrorCode(submitErrorCode, submitErrorMessage)}
          </InlineAlert>
        )}
      </div>
    );
  }

  function renderFooter(): JSX.Element {
    if (isEmpty) {
      return (
        <Button color="secondary" onPress={onCancel} data-testid="create-budget-empty-close">
          Zavřít
        </Button>
      );
    }
    return (
      <>
        <Button color="secondary" onPress={onCancel} isDisabled={submitting} data-testid="create-budget-cancel">
          Zrušit
        </Button>
        <Button
          color="primary"
          onPress={handleConfirm}
          isLoading={submitting}
          isDisabled={!selectedTarget || submitting || loading}
          data-testid="create-budget-confirm"
        >
          {submitting ? "Vytvářím…" : "Pokračovat"}
        </Button>
      </>
    );
  }

  return (
    <Modal
      open={open}
      title="Nový rozpočet — vyberte měsíc a Středisko"
      onClose={onCancel}
      footer={renderFooter()}
    >
      {renderBody()}
    </Modal>
  );
}
